import React from 'react';
import { observable, action } from 'mobx';
import { useObserver, observer } from 'mobx-react';
import { useViewerControl } from 'libs/rendering/gltf/hooks';

import keyMap from 'utils/keyMap';

import {
  TreeStyled,
  ClipListStyled,
  ClipPlaneStyled,
  ClipSliderStyled,
  ClipRowStyled,
  ButtonStyled,
  SliderWrapper,
  SliderInput,
  SliderButtonStyled,
  SlideStepButtonStyled,
  TextWrapper,
  InputStyled,
  SymbolWrapper,
  ThckValuesWrapper,
} from './sectionBrowser.styled';

const ANG_MAX = 90.0;

const CLIP_PAR = {
  PITCH: 0,
  ROLL: 1,
  OFFSET: 2,
};

class ClipPlaneState {
  @observable PITCH_TXT = '0';
  @observable ROLL_TXT = '0';
  @observable OFFSET_TXT = '0';
  @observable isDirty = false;

  PITCH = 0;
  ROLL = 0;
  OFFSET = 0;

  PITCH_NUM_DECIMALS = 2;
  ROLL_NUM_DECIMALS = 2;
  OFFSET_NUM_DECIMALS = 2;

  constructor(pitch, roll, offset) {
    this.PITCH = pitch;
    this.ROLL = roll;
    this.OFFSET = offset;

    this.PITCH_TXT = pitch.toFixed(this.PITCH_NUM_DECIMALS);
    this.ROLL_TXT = roll.toFixed(this.ROLL_NUM_DECIMALS);
    this.OFFSET_TXT = offset.toFixed(this.OFFSET_NUM_DECIMALS);
  }

  getPropText = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH_TXT;
      case CLIP_PAR.ROLL:
        return this.ROLL_TXT;
      case CLIP_PAR.OFFSET:
        return this.OFFSET_TXT;
      default:
        break;
    }
    return null;
  };

  getPropValue = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH;
      case CLIP_PAR.ROLL:
        return this.ROLL;
      case CLIP_PAR.OFFSET:
        return this.OFFSET;
      default:
        break;
    }
    return null;
  };

  @action setPropText = (propIx, txt) => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        this.PITCH_TXT = txt.toString();
        break;
      case CLIP_PAR.ROLL:
        this.ROLL_TXT = txt.toString();
        break;
      case CLIP_PAR.OFFSET:
        this.OFFSET_TXT = txt.toString();
        break;
      default:
        break;
    }
    this.isDirty = true;

    const isNumber = !Number.isNaN(txt) && txt.slice(-1) !== '.';
    const valNum = isNumber ? parseFloat(txt) : null;
    if (valNum) {
      this.setPropValue(propIx, valNum);
    }
  };

  getPropNumDecimals = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH_NUM_DECIMALS;
      case CLIP_PAR.ROLL:
        return this.ROLL_NUM_DECIMALS;
      case CLIP_PAR.OFFSET:
        return this.OFFSET_NUM_DECIMALS;
      default:
        break;
    }
    return 1;
  };

  setPropNumDecimals = (propIx, numDecimals) => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        this.PITCH_NUM_DECIMALS = numDecimals;
        break;
      case CLIP_PAR.ROLL:
        this.ROLL_NUM_DECIMALS = numDecimals;
        break;
      case CLIP_PAR.OFFSET:
        this.OFFSET_NUM_DECIMALS = numDecimals;
        break;
      default:
        break;
    }
  };

  @action setPropValue = (propIx, value, updateTxt = false) => {
    const numDecimals = this.getPropNumDecimals(propIx);
    const valStr = value.toFixed(numDecimals);
    const isNumber = !Number.isNaN(valStr);
    const valNum = isNumber ? parseFloat(valStr) : null;

    switch (propIx) {
      case CLIP_PAR.PITCH:
        if (isNumber) {
          if (updateTxt) this.PITCH_TXT = valStr;
          if (this.PICTH === valNum) return;
          this.PITCH = valNum;
        }
        break;
      case CLIP_PAR.ROLL:
        if (isNumber) {
          if (updateTxt) this.ROLL_TXT = valStr;
          if (this.ROLL === valNum) return;
          this.ROLL = valNum;
        }
        break;
      case CLIP_PAR.OFFSET:
        if (isNumber) {
          if (updateTxt) this.OFFSET_TXT = valStr;
          if (this.OFFSET === valNum) return;
          this.OFFSET = valNum;
        }
        break;
      default:
        break;
    }
    this.isDirty = false;
  };

  @action restoreProp = propIx => {
    this.setPropValue(propIx, this.getPropValue(propIx), true);
  };

  @action clear = () => {
    this.PITCH = 0;
    this.ROLL = 0;
    this.OFFSET = 0;

    this.PITCH_TXT = '0';
    this.ROLL_TXT = '0';
    this.OFFSET_TXT = '0';
  };

  isTemp = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH.toFixed(this.PITCH_NUM_DECIMALS) !== this.PITCH_TXT;
      case CLIP_PAR.ROLL:
        return this.ROLL.toFixed(this.ROLL_NUM_DECIMALS) !== this.ROLL_TXT;
      case CLIP_PAR.OFFSET:
        return (
          this.OFFSET.toFixed(this.OFFSET_NUM_DECIMALS) !== this.OFFSET_TXT
        );
      default:
        break;
    }
    return false;
  };
}

export class SectionState {
  @observable.ref sectionManager = null;
  @observable.ref engine = null;
  @observable.ref settings = null;
  @observable clips = new Map();
  @observable updated = false;

  @observable animatingProp = null;
  @observable animatingPropValue = null;

  constructor(sectionManager, engine, settings) {
    this.engine = engine;
    this.sectionManager = sectionManager;
    this.settings = settings;

    // permanent mouse down for tect offset
    this.sliderInterval = null;
    this.sliderIntervalMs = 100;
  }

  addClip = planeType => {
    const clipIx = this.sectionManager.GetSectionClipIx(planeType);

    if (!this.clips[clipIx]) {
      let pitch = 0;
      let roll = 0;
      let offset = 0;
      if (clipIx >= 1 && clipIx <= 3) {
        pitch = this.sectionManager.GetPitch(clipIx);
        roll = this.sectionManager.GetRoll(clipIx);
        offset = this.sectionManager.GetOffset(clipIx);
      }
      this.clips[clipIx] = new ClipPlaneState(pitch, roll, offset);
      this.updated = !this.updated;
    }

    return clipIx;
  };

  setNumDecimals = (clipIx, propIx, numDecimals) => {
    this.clips[clipIx].setPropNumDecimals(propIx, numDecimals);
  };

  getNumDecimals = (clipIx, propIx) => {
    if (this.clips.length > clipIx) {
      return this.clips[clipIx].getPropNumDecimals(propIx);
    }
    return 1;
  };

  @action toggleClip = (clipIx, planeType) => {
    if (!this.sectionManager.IsActive()) this.sectionManager.SetActive(true);

    if (clipIx < 0) {
      // no active plane for this type, so switch ON
      for (let i = 1; i <= 3; i++) {
        const clip = this.sectionManager.GetClipSection(i);
        if (!clip.apply) {
          // free plane, use it
          this.sectionManager.SwitchOnClipSection(planeType);
          this.engine.UpdateClipPlane(i, clip._wasm);
          break;
        }
      }
    } else {
      // switch OFF
      this.sectionManager.SwitchOffClipSection(clipIx);
      for (let i = 1; i <= 3; i++) {
        const clip = this.sectionManager.GetClipSection(i);
        this.engine.UpdateClipPlane(i, clip._wasm);
      }
      this.clips[clipIx]?.clear();
    }

    this.updated = !this.updated;
  };

  isClipVisible = clipIx => {
    if (this.sectionManager.IsRunning()) {
      if (clipIx >= 1 && clipIx <= 3) {
        const clip = this.sectionManager.GetClipSection(clipIx);
        return clip ? clip.visible : false;
      }
    }
    return false;
  };

  isClipActive = clipIx => {
    if (clipIx >= 1 && clipIx <= 3) {
      const clip = this.sectionManager.GetClipSection(clipIx);
      return clip ? clip.apply : false;
    }
    return false;
  };

  isClipThicknessCalculated = clipIx => {
    if (this.sectionManager.IsRunning()) {
      if (clipIx >= 1 && clipIx <= 3) {
        const clip = this.sectionManager.GetClipSection(clipIx);
        return clip ? clip.thicknessCalculated : false;
      }
    }
    return false;
  };

  getClipThicknessRange = clipIx => {
    if (clipIx >= 1 && clipIx <= 3) {
      const clip = this.sectionManager.GetClipSection(clipIx);
      return clip && clip.apply
        ? [clip.GetMinThickness(), clip.GetMaxThickness()]
        : [0, 0];
    }
    return [0, 0];
  };

  getClipThicknessPlotRange = clipIx => {
    if (clipIx >= 1 && clipIx <= 3) {
      const clip = this.sectionManager.GetClipSection(clipIx);
      return clip && clip.apply
        ? [clip.plotRangeMin, clip.plotRangeMax]
        : [0, 0];
    }
    return [0, 0];
  };

  getClipNormal = clipIx => {
    if (clipIx >= 1 && clipIx <= 3) {
      const clip = this.sectionManager.GetClipSection(clipIx);
      return clip && clip.apply ? clip.GetNormal() : null;
    }
    return null;
  };

  getClipOffsetRange = clipIx => {
    const camera = this.settings?.CurrentCamera();
    if (camera) {
      const dir = this.getClipNormal(clipIx);
      if (dir) {
        return camera.GetSceneSizeAtDir(dir.x, dir.y, dir.z);
      }
    }
    return 0;
  };

  @action toggleClipVisibility = clipIx => {
    if (!this.sectionManager.IsActive()) return;
    this.sectionManager.ToggleVisible(clipIx);
    this.updated = !this.updated;
  };

  @action reverseClip = clipIx => {
    if (!this.sectionManager.IsActive()) return;
    this.sectionManager.Reverse(clipIx);
    this.updated = !this.updated;
  };

  @action updateClip = (clipIx, propIx, magnitude, propVal) => {
    if (this.sectionManager.IsActive() === false) return;

    const val = parseFloat(magnitude);
    if (clipIx >= 1 && clipIx <= 3) {
      switch (propIx) {
        case CLIP_PAR.PITCH:
          this.sectionManager.RotatePitch(clipIx, val);
          break;
        case CLIP_PAR.ROLL:
          this.sectionManager.RotateRoll(clipIx, val);
          break;
        case CLIP_PAR.OFFSET:
          this.sectionManager.Offset(clipIx, val);
          break;
        default:
          break;
      }

      // update the shader uniforms
      const clip = this.sectionManager.GetClipSection(clipIx);
      this.engine.UpdateClipPlane(clipIx, clip._wasm);

      // update component state used to render slider values
      if (propVal) {
        this.setPropValue(clipIx, propIx, propVal);
      } else {
        this.setPropValue(clipIx, propIx, val);
      }
    }
  };

  // animation
  @action updateClipAnimate = (
    clipIx,
    propIx,
    start,
    direction,
    min,
    max,
    step,
  ) => {
    if (!step || !min || !max || min >= max)
      return this.updateClip(clipIx, propIx, start);
    if (direction !== '+' && direction != '-')
      return this.updateClip(clipIx, propIx, start);

    if (start == null) start = min; // from min to max

    this.animatingProp = propIx;
    this.sliderInterval = setInterval(() => {
      this.updateClip(clipIx, propIx, start);
      // next iteration or end
      if (direction === '+') {
        if (start + step <= max) start += step;
        else this.pauseClipAnimation();
      } else if (start - step >= min) start -= step;
      else this.pauseClipAnimation();
    }, this.sliderIntervalMs);
  };

  @action pauseClipAnimation = () => {
    clearInterval(this.sliderInterval);
    this.animatingProp = null;
    this.sectionManager?.reportWasmChange();
  };

  @action setPropText = (clipIx, propIx, txt) => {
    if (!this.clips[clipIx]) return;
    this.clips[clipIx].setPropText(propIx, txt);
    this.updated = !this.updated;
  };

  @action setPropValue = (clipIx, propIx, val) => {
    if (!this.clips[clipIx]) return;
    this.clips[clipIx].setPropValue(propIx, val, true);
    this.updated = !this.updated;
  };

  @action restoreProp = (clipIx, propIx) => {
    if (!this.clips[clipIx]) return;
    this.clips[clipIx].restoreProp(propIx);
    this.updated = !this.updated;
  };
}

const SectionBrowser = observer(() => {
  const viewerControl = useViewerControl();
  const { sectionState } = viewerControl.viewer;
  if (!sectionState) {
    return null;
  }

  return (
    <TreeStyled updated={sectionState.updated}>
      <ClipListStyled>
        <ClipPlane
          ix={1}
          sectionState={sectionState}
          clipType={viewerControl.defaultClipPlaneType}
        />
      </ClipListStyled>
    </TreeStyled>
  );
});

export default SectionBrowser;

const ClipPlane = observer(props => {
  const { ix, sectionState, clipType } = props;

  // initial values
  const clipIx = sectionState.addClip(clipType);

  const isClipActive = () => {
    return sectionState.isClipActive(clipIx);
  };

  const reverseClip = () => {
    sectionState.reverseClip(clipIx);
  };

  // render
  const thckPlotRange = sectionState.getClipThicknessPlotRange(clipIx);
  const thckRange = sectionState.getClipThicknessRange(clipIx);
  const thckHighlight =
    thckRange[0] <= thckPlotRange[0]
      ? 1
      : thckRange[0] <= thckPlotRange[1]
      ? 0.5
      : 0;
  const thckValue = thckRange[0].toFixed(4);

  const offsetRange =
    parseFloat((0.5 * sectionState.getClipOffsetRange(clipIx)).toFixed(1)) +
    0.1;
  const offsetStep = Math.max(
    parseFloat(
      (offsetRange / 1000).toFixed(
        sectionState.getNumDecimals(clipIx, CLIP_PAR.OFFSET),
      ),
    ),
    0.0001,
  );

  const viewerControl = useViewerControl();
  const currCamera = viewerControl?.viewer?.settings?.CurrentCamera();
  let unitLabel = viewerControl.cadFile?.unitLabel;
  if (currCamera.IsInstance()) {
    // single part: check if BREP or MESH
    const instance = currCamera?.GetInstance();
    const body = instance?.GetBody();
    if (body) {
      const hasSeparation = body.HasSeparation();
      if (!hasSeparation) unitLabel = '';
    }
  }

  return (
    <ClipPlaneStyled
      id={ix}
      active={isClipActive()}
      isDirty={sectionState.clips[clipIx].isDirty}
    >
      <ClipListStyled>
        {/* OFFSET */}
        <ClipSlider
          id={`offset_slider_${offsetRange}`}
          clipIx={clipIx}
          name='offset'
          symbol={unitLabel}
          sectionState={sectionState}
          propIx={CLIP_PAR.OFFSET}
          active={isClipActive()}
          min={-offsetRange}
          max={offsetRange}
          step={offsetStep}
        />
        {/* PICTH ANGLE */}
        <ClipSlider
          clipIx={clipIx}
          name='pitch'
          symbol='°'
          sectionState={sectionState}
          propIx={CLIP_PAR.PITCH}
          active={isClipActive()}
          min={-ANG_MAX}
          max={ANG_MAX}
          step={1}
        />
        {/* ROLL ANGLE */}
        <ClipSlider
          clipIx={clipIx}
          name='roll'
          symbol='°'
          sectionState={sectionState}
          propIx={CLIP_PAR.ROLL}
          active={isClipActive()}
          min={-ANG_MAX}
          max={ANG_MAX}
          step={1}
        />
        {/* reverse */}
        <ClipRowStyled active={true}>
          <div>
            <small>reverse</small>
          </div>
          <ButtonStyled
            variant='tertiary'
            ariaLabel='Reverse Clip'
            icon='flip_plane'
            size='default'
            iconViewBox='0 0 40 40'
            onClick={reverseClip}
            appearance='plain'
          />
          <div />

          {/* thickness  */}
          {thckRange[0] > 0 && (
            <>
              <ThckValuesWrapper
                highlight={thckHighlight}
                unitLabel={unitLabel}
              >
                <SlideStepButtonStyled
                  variant='tertiary'
                  ariaLabel='clear'
                  size='small'
                  icon='thickness'
                  iconViewBox='0 0 40 40'
                  appearance='plain'
                  removeBold
                />
                <div>
                  <small>{`${thckValue}`}</small>
                </div>
                <SymbolWrapper small={true}>{unitLabel}</SymbolWrapper>
              </ThckValuesWrapper>
            </>
          )}
        </ClipRowStyled>
      </ClipListStyled>
    </ClipPlaneStyled>
  );
});

function ClipSlider(props) {
  const { clipIx, name, symbol, propIx, active, max, min, sectionState, step } =
    props;

  let numDecimals = 2;
  if (Math.abs(max) < 1) {
    numDecimals = Math.ceil(-Math.log10(Math.abs(max))) + 1;
  }
  sectionState.setNumDecimals(clipIx, propIx, numDecimals);

  const clipPlaneChange = (magnitude, direction = null) => {
    if (direction) {
      // permanent mouse down
      sectionState.updateClipAnimate(
        clipIx,
        propIx,
        magnitude,
        direction,
        min,
        max,
        step,
      );
    } else {
      // mouse-click
      sectionState.pauseClipAnimation();
      sectionState.updateClip(clipIx, propIx, magnitude);
    }
  };

  const clipPlaneReset = () => {
    sectionState.pauseClipAnimation();
    clipPlaneChange(0);
  };

  const handleTextInput = e => {
    const isNumber =
      e.target.value.length > 0 &&
      e.target.value.slice(-1) !== '.' &&
      !Number.isNaN(e.target.value);
    const val = parseFloat(e.target.value);
    let txtValue = e.target.value;
    if (isNumber) {
      txtValue = val.toString();
      // check limits
      if (val < min) return;
      if (val > max) return;
    }
    // update the text...
    sectionState.pauseClipAnimation();
    sectionState.setPropText(clipIx, propIx, txtValue);
  };

  const onKeyDown = e => {
    const valTxt = sectionState.clips[clipIx].getPropText(propIx);
    const isNumber = valTxt.length > 0 && !Number.isNaN(valTxt);
    sectionState.pauseClipAnimation();

    switch (e.key.toUpperCase()) {
      case keyMap.ESCAPE:
        sectionState.restoreProp(clipIx, propIx);
        e.stopPropagation();
        break;
      case keyMap.ENTER:
        if (isNumber) {
          clipPlaneChange(parseFloat(valTxt));
        }
        e.stopPropagation();
        break;
      case keyMap.BACKSPACE:
        // deleting...
        break;
      default:
        break;
    }
  };

  const clipState = sectionState.clips[clipIx];
  if (!clipState) return null;
  let valNum = clipState.getPropValue(propIx);
  if (valNum > max) {
    sectionState.updateClip(clipIx, propIx, max);
    clipState.setPropValue(propIx, max, true);
    valNum = max;
  } else if (valNum < min) {
    // below commented lines trigger a mobx error. They're not needed anyway
    // sectionState.updateClip(clipIx, propIx, min);
    // clipState.setPropValue(propIx, min, true);
    valNum = min;
  }

  const valTxt = clipState.getPropText(propIx);
  const isTemp = clipState.isTemp(propIx);

  const isAnimatingProp = sectionState.animatingProp != null;
  const isAnimatingThisProp = sectionState.animatingProp === propIx;
  const animationIcon = !isAnimatingProp
    ? 'play'
    : isAnimatingThisProp
    ? 'pause'
    : 'more';
  const animationMsg = !isAnimatingProp
    ? 'start animation'
    : isAnimatingThisProp
    ? 'pause animation'
    : '';

  return useObserver(() => (
    <ClipSliderStyled active={active}>
      <div>
        <small>{name}</small>
      </div>
      <TextWrapper isTemp={isTemp}>
        <InputStyled
          name={name}
          value={valTxt}
          isTemp={isTemp}
          onChange={e => {
            handleTextInput(e);
          }}
          onKeyDown={e => {
            onKeyDown(e);
          }}
          type='text'
          size='small'
          isGhost
        />
      </TextWrapper>
      <SymbolWrapper small={symbol.length > 1}>{symbol}</SymbolWrapper>

      <SlideStepButtonStyled
        variant='minimal'
        ariaLabel='clear'
        size='xsmall'
        icon='caret_left'
        disabled={valNum - step <= min}
        onClick={() => {
          if (valNum - step >= min) clipPlaneChange(valNum - step);
        }}
        onMouseDown={() => {
          if (valNum - step >= min) clipPlaneChange(valNum - step, '-');
        }}
        onMouseUp={() => sectionState.pauseClipAnimation()}
        appearance='plain'
      />
      <SliderWrapper>
        <SliderInput
          active={active}
          onChange={e => {
            clipPlaneChange(e.target.value);
          }}
          type='range'
          value={valTxt}
          min={min}
          max={max}
          step={step}
        />
      </SliderWrapper>

      <SlideStepButtonStyled
        variant='tertiary'
        ariaLabel='clear'
        size='xsmall'
        icon='caret_right'
        disabled={valNum + step >= max}
        onClick={() => {
          if (valNum + step <= max) clipPlaneChange(valNum + step);
        }}
        onMouseDown={() => {
          if (valNum + step <= max) clipPlaneChange(valNum + step, '+');
        }}
        onMouseUp={() => sectionState.pauseClipAnimation()}
        appearance='plain'
      />
      <SliderButtonStyled
        variant='tertiary'
        ariaLabel='clear'
        size='small'
        icon='undo'
        onClick={clipPlaneReset}
        appearance='plain'
      />
      <SliderButtonStyled
        variant='tertiary'
        ariaLabel={animationMsg}
        size='small'
        icon={animationIcon}
        iconViewBox={!isAnimatingProp ? '0 0 24 24' : '0 0 40 40'}
        onClick={() => {
          if (!isAnimatingProp) {
            sectionState.updateClipAnimate(
              clipIx,
              propIx,
              valNum,
              '+',
              min,
              max,
              step,
            );
          } else if (isAnimatingThisProp) {
            sectionState.pauseClipAnimation();
          }
        }}
        appearance='plain'
      />
    </ClipSliderStyled>
  ));
}
